import type { GapRunValidation, RunGap } from '@momentum/types';

/**
 * The finder's four scoring axes, in the order the validator reports them.
 *
 * These are the run's own scores and belong to the pipeline zone — they are shown
 * in the inspector rail and never in the reader, where the product's own
 * `severity` is the only score. See `product-value.ts`.
 */
export const SCORE_ORDER = [
  'impact',
  'urgency',
  'missability',
  'customerFriction',
] as const;

export type ScoreAxis = (typeof SCORE_ORDER)[number];

/** `customerFriction` is abbreviated because the rail's key column is narrow. */
export const SCORE_LABEL: Record<ScoreAxis, string> = {
  impact: 'impact',
  urgency: 'urgency',
  missability: 'missability',
  customerFriction: 'cust_friction',
};

/**
 * Every value the four axes can take, weakest first.
 *
 * `NONE` is only ever a revised value: the validator uses it on a CONTRADICTED
 * gap, which has no residual impact left to weigh. `EXTREME` is only ever a
 * missability. One shared ladder covers all four axes because comparing a
 * position on it is the only thing the ladder is used for.
 */
const SCORE_SCALE = ['NONE', 'LOW', 'MEDIUM', 'HIGH', 'EXTREME'];

/**
 * The rail shows a move as `MED → HIGH`, so the long values are clipped.
 *
 * The two half-steps are only ever a `GapRunConfidence`, which the rail shows
 * beside the four axes even though no validator revision moves it.
 */
export function shortScore(value: string): string {
  if (value === 'MEDIUM') return 'MED';
  if (value === 'EXTREME') return 'EXTR';
  if (value === 'LOW_MEDIUM') return 'LOW-MED';
  if (value === 'MEDIUM_HIGH') return 'MED-HIGH';
  return value;
}

export type ScoreDeltaDirection = 'up' | 'down' | 'none';

export interface ScoreDelta {
  axis: ScoreAxis;
  before: string;
  after: string;
  direction: ScoreDeltaDirection;
}

const REVISED_KEY: Record<ScoreAxis, keyof GapRunValidation> = {
  impact: 'revisedImpact',
  urgency: 'revisedUrgency',
  missability: 'revisedMissability',
  customerFriction: 'revisedCustomerFriction',
};

/**
 * What the validator did to each of the finder's four scores.
 *
 * A gap whose scores all survived adjudication is the common case and is worth
 * saying explicitly, so the caller gets one row per axis either way and decides
 * how to collapse them.
 */
export function scoreDeltas(
  source: RunGap,
  validation: GapRunValidation
): ScoreDelta[] {
  return SCORE_ORDER.map((axis) => {
    const before = source[axis];
    const after = String(validation[REVISED_KEY[axis]]);
    const direction: ScoreDeltaDirection =
      before === after
        ? 'none'
        : SCORE_SCALE.indexOf(after) > SCORE_SCALE.indexOf(before)
        ? 'up'
        : 'down';

    return { axis, before, after, direction };
  });
}
